import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Clock,
  UserX,
  Layers,
  Bell,
  Search,
  CheckCircle2,
  ShieldCheck,
  Target,
  UserCheck,
  Handshake,
  ClipboardList,
  ChevronDown,
  ArrowRight,
  MessageSquare,
  BarChart3,
  PhoneCall,
  ShieldAlert,
  HeartPulse,
  TrendingUp,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

const painPoints = [
  {
    icon: AlertTriangle,
    title: "Ubesatte vagter",
    description:
      "Akutte sygemeldinger der efterlader jer med huller i vagtplanen og presset personale.",
  },
  {
    icon: Clock,
    title: "Langsom responstid",
    description:
      "Vikarbureauer der bruger timer på at finde en erstatning — når I har brug for svar inden for minutter.",
  },
  {
    icon: UserX,
    title: "Forkert match",
    description:
      "Vikarer uden de rette kompetencer eller erfaring fra jeres type institution.",
  },
  {
    icon: Layers,
    title: "Administrativt bøvl",
    description:
      "Endeløs koordinering, opkald og emails for at få dagligdagen til at hænge sammen.",
  },
];

const solutionSteps = [
  {
    icon: Bell,
    title: "En vagt bliver ledig",
    description:
      "En vagt skal dækkes — akut eller planlagt. Vores system opdager det på sekunder.",
  },
  {
    icon: Search,
    title: "Vi matcher",
    description:
      "Vi finder den rette profil baseret på kompetencer, erfaring og jeres præferencer.",
  },
  {
    icon: CheckCircle2,
    title: "Bekræftelse",
    description:
      "I får besked med navn og kvalifikationer. Vi bekræfter med vikaren og sikrer fremmødet.",
  },
  {
    icon: ShieldCheck,
    title: "Levering",
    description:
      "En kvalificeret og forberedt vikar møder op til tiden. Hver gang.",
  },
];

const benefits = [
  {
    icon: ShieldCheck,
    title: "Kvalitetssikret personale",
    description:
      "Alle vikarer er verificerede med gyldige autorisationer, relevante kompetencer og erfaring fra den kommunale plejesektor.",
  },
  {
    icon: Clock,
    title: "Akut og planlagt dækning",
    description:
      "Vi dækker både akutte vagter med kort varsel og langsigtet bemanding. Dag, aften og nattevagter.",
  },
  {
    icon: Target,
    title: "Kompetencematch",
    description:
      "Vi matcher på faglige kompetencer som medicin, sårpleje, kateter, stomi, diabetes, demens og palliation.",
  },
  {
    icon: UserCheck,
    title: "Dedikeret kontaktperson",
    description:
      "En fast kontaktperson der kender jeres institution, jeres borgere og jeres behov.",
  },
  {
    icon: Handshake,
    title: "Langsigtede samarbejder",
    description:
      "Vi tror på faste relationer. Jeres borgere fortjener genkendelighed og kontinuitet i plejen.",
  },
  {
    icon: ClipboardList,
    title: "Fuld sporbarhed",
    description:
      "Komplet overblik over planlagte og gennemførte vagter. Ingen overraskelser ved fakturering.",
  },
];

const shiftTypes = [
  "Dagvagter",
  "Aftenvagter",
  "Nattevagter",
  "Weekendvagter",
  "Helligdagsvagter",
  "Akutte vagter (kort varsel)",
];

const staffTypes = [
  { title: "Sygeplejersker", short: "SPL" },
  { title: "Social- og sundhedsassistenter", short: "SSA" },
  { title: "Social- og sundhedshjælpere", short: "SSH" },
];

const faqs = [
  {
    q: "Hvor hurtigt kan I reagere på ledige vagter?",
    a: "Vores system reagerer på sekunder. Når en ledig vagt opstår, matcher vi øjeblikkeligt med en kvalificeret medarbejder fra vores team.",
  },
  {
    q: "Hvordan sikrer I kvaliteten af jeres vikarer?",
    a: "Alle vikarer gennemgår en grundig screening med verifikation af autorisationer, kompetenceskema og personlig samtale. Vi matcher på faglige kompetencer, erfaring og institutionstype.",
  },
  {
    q: "Kan vi få de samme vikarer fast?",
    a: "Ja, det er faktisk vores mål. Vi tror på kontinuitet og faste relationer. Vi prioriterer at sende de samme vikarer til jeres institution, så borgerne oplever genkendelighed.",
  },
  {
    q: "Hvad koster det at bruge Plejesøstrene?",
    a: "Vi tilbyder konkurrencedygtige priser med fuld gennemsigtighed. Kontakt os for et tilbud tilpasset jeres behov og volumen.",
  },
  {
    q: "Dækker I også langsigtet bemanding?",
    a: "Ja. Udover akut vikardækning tilbyder vi også længerevarende bemanding til planlagte vakancer, barselsvikariater og ferieperioder.",
  },
  {
    q: "Hvad sker der hvis en vikar melder sig syg?",
    a: "Vi finder hurtigst muligt en erstatning. Vores system gør det muligt at reagere hurtigt, så I aldrig står uden dækning.",
  },
  {
    q: "Sender I vikarer på dobbeltvagter?",
    a: "Kun som absolut sidste udvej. Først vurderer vi om andre bureauer kan dække vagten — det kan vi aflæse af markedet. Hvis mange vagter har stået ledige længe, er der sandsynligvis ingen andre der kan tage den, og så træder vi til. Hvis vagter derimod bliver taget hurtigt, slipper vi den, fordi andre bureauer har kapacitet. Dernæst vurderer vi om vikaren reelt kan klare det forsvarligt. Kun hvis ingen andre kan dække og vi ikke har en forsvarlig løsning, annullerer vi vagten. Når vi leverer, mærker hverken I eller borgerne til noget — og vikaren får fri dagen efter.",
  },
];

export default function ForInstitutionerPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      {/* Hero */}
      <section
        className="section relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #e8f9fc 0%, #ffffff 40%, #eef2ff 100%)",
        }}
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-100 rounded-full -translate-y-1/2 translate-x-1/3 opacity-40 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent-100 rounded-full translate-y-1/3 -translate-x-1/4 opacity-30 blur-3xl pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            transition={{ staggerChildren: 0.1 }}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
              <span className="kicker">For institutioner</span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.5 }}
              className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight text-slate-900"
            >
              Pålidelig vikardækning —{" "}
              <span className="text-brand-500 subtle-glow">
                hurtigt, kvalitetssikret og uden bøvl
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.5 }}
              className="mt-5 text-slate-500 text-base sm:text-lg max-w-2xl leading-relaxed"
            >
              Vi forstår hverdagen på plejehjem, i hjemmeplejen og på bosteder.
              Derfor leverer vi ikke bare vikarer — vi leverer tryghed og
              kontinuitet.
            </motion.p>

            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.5 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <Link to="/kontakt" className="btn btn-primary btn-lg">
                Kontakt os for et samarbejde
                <ArrowRight size={18} />
              </Link>
              <a
                href="tel:+4589801515"
                className="btn btn-secondary btn-lg"
              >
                Ring til os: 89 80 15 15
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Problemet vi løser */}
      <section className="section py-10 sm:py-14 md:py-18 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="kicker">Udfordringen</span>
            <h2 className="section-title mt-2">
              Udfordringer vi kender fra plejesektoren
            </h2>
            <p className="section-subtitle">
              Mange institutioner kæmper med de samme problemer. Vi har
              løsningen.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 gap-5">
            {painPoints.map((p) => (
              <div key={p.title} className="card p-5 flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                  <p.icon className="text-red-500" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm">
                    {p.title}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500 leading-relaxed">
                    {p.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vores løsning - 4 trin */}
      <section className="section py-10 sm:py-14 md:py-18 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-12">
            <span className="kicker">Vores løsning</span>
            <h2 className="section-title mt-2">
              Sådan løser vi jeres vikarbehov
            </h2>
            <p className="section-subtitle mx-auto">
              En enkel og effektiv proces fra behov til dækning.
            </p>
          </div>

          {/* Desktop: Horizontal */}
          <div className="hidden md:block">
            <div className="relative">
              <div
                className="absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-brand-200 via-brand-400 to-brand-600"
                style={{ zIndex: 0 }}
              />
              <div
                className="relative grid grid-cols-4 gap-6"
                style={{ zIndex: 1 }}
              >
                {solutionSteps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <div
                      key={step.title}
                      className="flex flex-col items-center"
                    >
                      <div className="relative w-16 h-16 rounded-full bg-white border-2 border-brand-500 flex items-center justify-center mb-4 shadow-card">
                        <Icon
                          className="text-brand-600"
                          size={24}
                          strokeWidth={2}
                        />
                        <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center">
                          {index + 1}
                        </span>
                      </div>
                      <h3 className="font-bold text-sm text-slate-900 text-center mb-1">
                        {step.title}
                      </h3>
                      <p className="text-xs text-slate-500 text-center leading-relaxed max-w-[200px]">
                        {step.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mobile: Vertical */}
          <div className="md:hidden space-y-4">
            {solutionSteps.map((step, index) => {
              const Icon = step.icon;
              const isLast = index === solutionSteps.length - 1;
              return (
                <div key={step.title} className="relative">
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="relative w-14 h-14 rounded-full bg-white border-2 border-brand-500 flex items-center justify-center shadow-card">
                        <Icon
                          className="text-brand-600"
                          size={22}
                          strokeWidth={2}
                        />
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-brand-500 text-white text-[10px] font-bold flex items-center justify-center">
                          {index + 1}
                        </span>
                      </div>
                      {!isLast && (
                        <div
                          className="w-0.5 flex-1 bg-gradient-to-b from-brand-400 to-brand-200 mt-2 mb-2"
                          style={{ minHeight: "20px" }}
                        />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <h3 className="font-bold text-base text-slate-900 mb-1">
                        {step.title}
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fordele */}
      <section className="section py-10 sm:py-14 md:py-18 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="kicker">Jeres fordele</span>
            <h2 className="section-title mt-2">
              Hvad I får med Plejesøstrene
            </h2>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="card-elevated p-6">
                <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center mb-4">
                  <b.icon className="text-accent-600" size={20} />
                </div>
                <h3 className="font-semibold text-slate-900 text-base">
                  {b.title}
                </h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  {b.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pålidelighed i praksis */}
      <section className="section py-10 sm:py-14 md:py-18 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="kicker">Pålidelighed i praksis</span>
            <h2 className="section-title mt-2">
              Tager vi en vagt — kan vi også levere
            </h2>
            <p className="section-subtitle">
              Vi har bygget konkrete processer der sikrer at I aldrig oplever
              ubehagelige overraskelser. Her er hvordan.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <MessageSquare className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Morgenbekræftelse via SMS
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Kl. 05:30 sender vi en SMS til alle vikarer der har gjort sig
                tilgængelige til morgenvagter. De skal aktivt svare ja eller nej
                for at bekræfte. På den måde kender vi den reelle kapacitet —
                ingen gætværk, ingen fejlregistreringer.
              </p>
            </div>

            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <BarChart3 className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Reel kapacitet, ikke ønsketænkning
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Vi byder kun på vagter vi reelt kan dække. Vores system
                afstemmer løbende tilgængelighed, kompetencer og bekræftelser
                — så når vi siger ja til en vagt, kan I regne med at den er
                dækket.
              </p>
            </div>

            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <PhoneCall className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Automatisk opfølgning
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Hvis en vikar ikke bekræfter til tiden, eskalerer systemet
                automatisk og finder en erstatning. I hører kun fra os hvis
                der er noget I skal vide — ellers kører det bare.
              </p>
            </div>

            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <ShieldAlert className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Kvalitet over kvantitet
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Vi undlader hellere at byde på en vagt end at sende den
                forkerte person. Vores vikarer evalueres løbende, og vi
                prioriterer stabile, ansvarlige og kompetente medarbejdere til
                jeres institution.
              </p>
            </div>

            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <HeartPulse className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Forebygger udbrændthed
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Vi overvåger vores vikares arbejdsintensitet og tildeler maks
                én vagt per dag. Det sikrer at den vikar der møder op hos jer
                altid er udhvilet og klar til at yde sit bedste.
              </p>
            </div>

            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <TrendingUp className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Voksende kapacitet
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Vores team vokser støt, og vi investerer i at rekruttere og
                udvikle dygtige vikarer. Jo større kapacitet, jo bedre dækning
                — men altid i et tempo hvor kvaliteten kan følge med.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vagttyper og faggrupper */}
      <section className="section py-10 sm:py-14 md:py-18 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <span className="kicker">Dækning</span>
            <h2 className="section-title mt-2">Vagttyper og faggrupper</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-8">
            {/* Vagttyper */}
            <div className="card-elevated p-6">
              <h3 className="font-semibold text-slate-900 text-base mb-4">
                Vagttyper vi dækker
              </h3>
              <ul className="space-y-2.5">
                {shiftTypes.map((type) => (
                  <li
                    key={type}
                    className="flex items-center gap-2.5 text-sm text-slate-600"
                  >
                    <CheckCircle2
                      className="text-brand-500 shrink-0"
                      size={16}
                    />
                    {type}
                  </li>
                ))}
              </ul>
            </div>

            {/* Faggrupper */}
            <div className="card-elevated p-6">
              <h3 className="font-semibold text-slate-900 text-base mb-4">
                Faggrupper
              </h3>
              <ul className="space-y-2.5">
                {staffTypes.map((type) => (
                  <li
                    key={type.short}
                    className="flex items-center gap-2.5 text-sm text-slate-600"
                  >
                    <CheckCircle2
                      className="text-brand-500 shrink-0"
                      size={16}
                    />
                    {type.title}{" "}
                    <span className="text-xs text-slate-400">
                      ({type.short})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section py-10 sm:py-14 md:py-18 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="kicker">Ofte stillede spørgsmål</span>
            <h2 className="section-title mt-2">Det vil I måske gerne vide</h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="card overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-semibold text-slate-900 text-sm sm:text-base pr-4">
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`shrink-0 text-slate-400 transition-transform duration-200 ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                    size={20}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-sm text-slate-500 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="section relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #e8f9fc 0%, #ffffff 40%, #eef2ff 100%)",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
            Klar til stabil og pålidelig vikardækning?
          </h2>
          <p className="mt-4 text-slate-500 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            Lad os tage en uforpligtende snak om jeres behov.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/kontakt" className="btn btn-primary btn-lg">
              Kontakt os i dag
              <ArrowRight size={18} />
            </Link>
          </div>
          <p className="mt-4 text-sm text-slate-400">
            Eller ring direkte:{" "}
            <a
              href="tel:+4589801515"
              className="font-semibold text-brand-600 hover:underline"
            >
              89 80 15 15
            </a>
          </p>
        </div>
      </section>
    </>
  );
}
